import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Cpu } from "lucide-react";
import { Button } from "../components/ui/button";
import { api } from "../lib/site";

export default function PaymentReturnPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("polling"); // polling | paid | pending | failed
  const [txn, setTxn] = useState(null);
  const [error, setError] = useState("");
  const polledRef = useRef(false);

  useEffect(() => {
    if (polledRef.current) return;
    polledRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("failed");
      setError("Missing session id");
      return;
    }

    let attempts = 0;
    const max = 8;

    const tick = async () => {
      attempts += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        setTxn(res.data);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (res.data.status === "expired" || res.data.payment_status === "unpaid") {
          setStatus("failed");
          setError("Payment did not complete.");
          return;
        }
        if (attempts >= max) {
          // Pending after 8 polls — webhook will catch up. Tell the user we got the payment in motion.
          setStatus("pending");
          return;
        }
        setTimeout(tick, 2000);
      } catch (e) {
        // Soft-fail on transient errors; keep polling instead of bailing.
        if (attempts >= max) {
          setStatus("pending");
        } else {
          setTimeout(tick, 2000);
        }
      }
    };

    tick();
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center px-6 py-16" data-testid="payment-return-page">
      <Link to="/" className="absolute top-6 left-6 flex items-center gap-2">
        <img src="/logo.png" alt="Tombs Electronic Repair" className="h-10 w-auto object-contain" />
      </Link>

      <div className="w-full max-w-lg bg-[#121212] border border-zinc-800 rounded-md p-10 text-center">
        {status === "polling" && (
          <>
            <Loader2 className="w-12 h-12 text-[#0055FF] mx-auto mb-6 animate-spin" />
            <h1 className="font-display font-bold text-3xl tracking-tighter mb-3">Confirming payment…</h1>
            <p className="text-zinc-400">Hang tight — this usually takes a few seconds.</p>
          </>
        )}

        {status === "paid" && txn && (
          <>
            <CheckCircle2 className="w-14 h-14 text-[#34C759] mx-auto mb-6" />
            <div className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 mb-3">PAYMENT COMPLETE</div>
            <h1 className="font-display font-bold text-3xl tracking-tighter mb-4" data-testid="payment-success-heading">
              ${(txn.amount_usd).toFixed(2)} received
            </h1>
            <p className="text-zinc-400 mb-2">{txn.label}</p>
            <p className="text-zinc-500 font-mono text-xs tracking-[0.15em] uppercase mb-8">
              Ticket <span className="text-[#FFCC00]">{txn.ticket_id}</span>
            </p>
            <p className="text-zinc-400 text-sm mb-8 leading-relaxed">
              Thanks — you'll get a confirmation email from Stripe shortly. I'll be in touch with next steps on your repair.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => navigate(`/?ticket=${txn.ticket_id}#track`)}
                variant="outline"
                className="bg-transparent border-zinc-700 text-white hover:bg-white/5 hover:text-white"
                data-testid="payment-track-btn"
              >
                Track repair
              </Button>
              <Button
                onClick={() => navigate("/")}
                className="bg-[#0055FF] hover:bg-blue-600 text-white font-semibold"
                data-testid="payment-home-btn"
              >
                Back to site
              </Button>
            </div>
          </>
        )}

        {status === "pending" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-[#FFCC00] mx-auto mb-6" />
            <div className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 mb-3">PAYMENT RECEIVED</div>
            <h1 className="font-display font-bold text-3xl tracking-tighter mb-4">Got it — confirmation incoming</h1>
            <p className="text-zinc-400 mb-8 leading-relaxed">
              Your payment is being finalized. You'll get a confirmation email from Stripe in a moment.
              You can close this page; nothing else to do on your end.
            </p>
            <Button
              onClick={() => navigate("/")}
              className="bg-[#0055FF] hover:bg-blue-600 text-white font-semibold"
              data-testid="payment-pending-home-btn"
            >
              Back to site
            </Button>
          </>
        )}

        {status === "failed" && (
          <>
            <XCircle className="w-14 h-14 text-[#FF3B30] mx-auto mb-6" />
            <h1 className="font-display font-bold text-3xl tracking-tighter mb-4">Hmm, something went sideways</h1>
            <p className="text-zinc-400 mb-8">{error || "We couldn't confirm your payment."}</p>
            <Button
              onClick={() => navigate("/")}
              className="bg-[#0055FF] hover:bg-blue-600 text-white font-semibold"
            >
              Return home
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
